import { useEffect, useRef, useState } from 'react';
import debounce from 'lodash.debounce';
import { SYS_SPACE_FIXED_300, SYS_SIZE_FIXED_2000 } from '@oneaudi/unified-web-alpha-common';
const updatePopoverPosition = ({ ref, triggerRef, alignment, withTip, placement, mobileMaxContent }) => {
    const popover = ref.current;
    const trigger = triggerRef.current;
    if (!popover || !trigger)
        return;
    const root = document.documentElement;
    const pM = parseInt(window.getComputedStyle(root).getPropertyValue('--page-margin'));
    popover.style.left = '';
    popover.style.right = '';
    const BREAKPOINT_MD = 768;
    const triggerRect = trigger.getBoundingClientRect();
    const popoverRect = popover.getBoundingClientRect();
    const windowInnerWidth = window.innerWidth;
    const windowInnerHeight = window.innerHeight;
    // Tip should be disabled on small screens
    const tipEnabled = Boolean(withTip) && windowInnerWidth >= BREAKPOINT_MD;
    if (!tipEnabled) {
        popover.style.setProperty('--tip-left', 'unset');
        popover.style.setProperty('--tip-right', 'unset');
        popover.style.setProperty('--tip-top', 'unset');
    }
    const triggerLeft = Math.round(triggerRect.left);
    const triggerRight = Math.round(triggerRect.right);
    const triggerWidth = Math.round(triggerRect.width);
    const popoverWidth = Math.round(popoverRect.width);
    // TODO: Update pageMarginOffset when design supplies value
    const pageMarginOffset = 0;
    const tipWidth = 23;
    const tipHeight = 11.5;
    const tipEdgePadding = SYS_SPACE_FIXED_300;
    const tipEdgePaddingPx = typeof tipEdgePadding === 'number'
        ? tipEdgePadding
        : parseInt(String(tipEdgePadding), 10);
    const clampTipOffsetX = (desiredOffset) => {
        const max = popoverWidth - tipEdgePaddingPx - tipWidth;
        // If the popover is too narrow to respect the edge padding on both sides,
        // center the tip as a graceful fallback.
        if (max <= tipEdgePaddingPx) {
            return Math.max(0, Math.round((popoverWidth - tipWidth) / 2));
        }
        return Math.min(Math.max(desiredOffset, tipEdgePaddingPx), max);
    };
    let leftPosition = triggerLeft;
    let rightPosition = windowInnerWidth - triggerRight;
    const tipTriggerAlignValue = triggerWidth / 2 - tipWidth / 2;
    const getTipCentered = (tipLeft) => tipLeft + tipTriggerAlignValue;
    const popoverOffset = windowInnerWidth - popoverWidth;
    let popoverTop;
    let actualPlacement;
    const tipOffset = 20;
    const tipBottomPlacement = -tipHeight;
    // Measure available raw vertical space (excluding popover height) on each side
    const spaceAbove = triggerRect.top - tipOffset; // distance from top edge that content can occupy
    const spaceBelow = windowInnerHeight - (triggerRect.bottom + tipOffset);
    const fitsAbove = popoverRect.height <= spaceAbove;
    const fitsBelow = popoverRect.height <= spaceBelow;
    // Minimum scrollable region threshold: if the chosen placement (top, bottom) would yield a scroll container too small
    // to be useful, and the opposite placement can provide a reasonable region, prefer flipping.
    // Use 3lh: three times the computed line-height of the popover element
    const computedLineHeight = parseFloat(window.getComputedStyle(popover).lineHeight || '0');
    const fallbackPx = parseFloat(SYS_SIZE_FIXED_2000);
    const lineHeightValid = Number.isFinite(computedLineHeight) && computedLineHeight > 0;
    // If we have a valid computed line-height, require 3 * line-height.
    // Otherwise fall back to a single token size (fallbackPx)
    const MIN_SCROLLABLE_PX = Math.max(0, Math.floor(lineHeightValid ? 3 * computedLineHeight : fallbackPx));
    // Vertical padding is defined by design token SYS_SPACE_FIXED_300 on both top and bottom.
    const spaceToken = SYS_SPACE_FIXED_300;
    const spaceTokenPx = typeof spaceToken === 'number'
        ? spaceToken
        : parseInt(String(spaceToken), 10) || 0;
    const verticalPadding = 2 * spaceTokenPx;
    const contentSpaceAbove = Math.max(0, spaceAbove - verticalPadding);
    const contentSpaceBelow = Math.max(0, spaceBelow - verticalPadding);
    const hasViableAbove = contentSpaceAbove >= MIN_SCROLLABLE_PX;
    const hasViableBelow = contentSpaceBelow >= MIN_SCROLLABLE_PX;
    // Decide desired placement at measurement time.
    let placeAbove;
    if (placement === 'top') {
        if (fitsAbove) {
            placeAbove = true;
        }
        else if (fitsBelow) {
            placeAbove = false;
        }
        else {
            placeAbove = spaceAbove >= spaceBelow; // choose larger space if neither fits fully
        }
        // If we decided top but the usable space above is below threshold while below is viable, flip to bottom.
        if (placeAbove && !hasViableAbove && hasViableBelow) {
            placeAbove = false;
        }
    }
    else {
        if (fitsBelow) {
            placeAbove = false;
        }
        else if (fitsAbove) {
            placeAbove = true;
        }
        else {
            placeAbove = spaceAbove > spaceBelow; // equal favors bottom
        }
        // If we decided bottom but spaceBelow is below threshold and above is viable, flip to top.
        if (!placeAbove && !hasViableBelow && hasViableAbove) {
            placeAbove = true;
        }
    }
    if (placeAbove) {
        const desiredTop = triggerRect.top - tipOffset - popoverRect.height;
        if (desiredTop < 0) {
            // Fallback: top placement would overflow above the viewport and overlap the trigger when clamped.
            // Switch to bottom placement for a clearer visual relationship and correct tip positioning.
            placeAbove = false; // mutate for downstream availableHeight logic
            popoverTop = triggerRect.bottom + tipOffset;
            actualPlacement = 'bottom';
        }
        else {
            popoverTop = desiredTop;
            actualPlacement = 'top';
        }
    }
    else {
        popoverTop = triggerRect.bottom + tipOffset;
        actualPlacement = 'bottom';
    }
    // Clamp so popover never renders with a negative top (off the top of the viewport)
    if (popoverTop < 0) {
        popoverTop = 0;
    }
    popover.style.top = `${popoverTop}px`;
    // Provide the appropriate available height for the chosen placement; inner content will clamp to this.
    const availableHeight = placeAbove
        ? Math.max(0, Math.floor(spaceAbove))
        : Math.max(0, Math.floor(spaceBelow));
    popover.style.setProperty('--available-height', `${availableHeight}px`);
    // If top placement, we need the final clamped height before positioning the arrow at the bottom edge.
    if (tipEnabled) {
        if (actualPlacement === 'top') {
            // Re-measure after height clamp to get final height (may be reduced from natural height when oversized)
            const finalHeight = popover.getBoundingClientRect().height;
            popover.style.setProperty('--tip-top', `${finalHeight}px`);
        }
        else {
            popover.style.setProperty('--tip-top', `${tipBottomPlacement}px`);
        }
    }
    else {
        popover.style.setProperty('--tip-top', 'unset');
    }
    if (windowInnerWidth < BREAKPOINT_MD && !mobileMaxContent) {
        popover.style.left = `${pM + pageMarginOffset}px`;
        popover.style.right = `${pM + pageMarginOffset}px`;
        return actualPlacement;
    }
    switch (alignment) {
        case 'left': {
            leftPosition = triggerLeft;
            if (leftPosition + popoverWidth > windowInnerWidth) {
                leftPosition = popoverOffset;
            }
            if (leftPosition < 0) {
                leftPosition = 0;
            }
            popover.style.left = `${leftPosition}px`;
            if (tipEnabled) {
                // Compute desired/clamped tip offset
                let desiredLeft = triggerLeft - leftPosition + tipTriggerAlignValue;
                let clampedLeft = clampTipOffsetX(desiredLeft);
                // Shift popover left by the absolute difference
                const shift = Math.abs(clampedLeft - desiredLeft);
                if (shift > 0) {
                    const minLeft = 0;
                    const maxLeft = windowInnerWidth - popoverWidth;
                    leftPosition = Math.min(Math.max(leftPosition - shift, minLeft), maxLeft);
                    popover.style.left = `${leftPosition}px`;
                    // Recalculate offsets after shifting
                    desiredLeft = triggerLeft - leftPosition + tipTriggerAlignValue;
                    clampedLeft = clampTipOffsetX(desiredLeft);
                }
                popover.style.setProperty('--tip-right', 'unset');
                popover.style.setProperty('--tip-left', `${clampedLeft}px`);
            }
            break;
        }
        case 'right': {
            rightPosition = windowInnerWidth - triggerRight;
            if (rightPosition + popoverWidth > windowInnerWidth) {
                rightPosition = popoverOffset;
            }
            if (rightPosition < 0) {
                rightPosition = 0;
            }
            popover.style.right = `${rightPosition}px`;
            if (tipEnabled) {
                let desiredRight = windowInnerWidth -
                    (rightPosition + triggerRight) +
                    tipTriggerAlignValue;
                let clampedRight = clampTipOffsetX(desiredRight);
                // Shift popover right by the absolute difference
                const shift = Math.abs(clampedRight - desiredRight);
                if (shift > 0) {
                    const minRight = 0;
                    const maxRight = popoverOffset;
                    rightPosition = Math.min(Math.max(rightPosition - shift, minRight), maxRight);
                    popover.style.right = `${rightPosition}px`;
                    // Recalculate offsets after shifting
                    desiredRight =
                        windowInnerWidth -
                            (rightPosition + triggerRight) +
                            tipTriggerAlignValue;
                    clampedRight = clampTipOffsetX(desiredRight);
                }
                popover.style.setProperty('--tip-left', 'unset');
                popover.style.setProperty('--tip-right', `${clampedRight}px`);
            }
            break;
        }
        case 'center': {
            let leftPosForCenteredPopover = triggerLeft + triggerWidth / 2 - popoverWidth / 2;
            if (leftPosForCenteredPopover + popoverWidth > windowInnerWidth) {
                leftPosForCenteredPopover = popoverOffset;
            }
            if (leftPosForCenteredPopover < 0) {
                leftPosForCenteredPopover = 0;
            }
            popover.style.left = `${leftPosForCenteredPopover}px`;
            if (tipEnabled) {
                const tipLeft = triggerLeft - leftPosForCenteredPopover;
                const desiredLeft = getTipCentered(tipLeft);
                const clampedLeft = clampTipOffsetX(desiredLeft);
                popover.style.setProperty('--tip-right', 'unset');
                popover.style.setProperty('--tip-left', `${clampedLeft}px`);
            }
            break;
        }
    }
    // Final safeguard: if after horizontal positioning the popover bottom extends beyond the viewport,
    // shrink the available height variable so inner scroll area clamps further without moving the anchor.
    const finalRect = popover.getBoundingClientRect();
    const overflowBottom = finalRect.bottom - windowInnerHeight;
    if (overflowBottom > 0) {
        const adjustedAvailable = Math.max(0, finalRect.height - overflowBottom);
        // Only update if this is actually smaller; prevents oscillation.
        const currentAvail = parseInt(popover.style.getPropertyValue('--available-height') || '0', 10);
        if (adjustedAvailable < currentAvail) {
            popover.style.setProperty('--available-height', `${Math.max(0, Math.floor(adjustedAvailable))}px`);
        }
    }
    // Normalization pass to correct any overlap caused by first-pass zero-height or late content expansion.
    // Recalculate final height (may have changed after available-height clamp) and enforce the intended gap.
    {
        const normRect = popover.getBoundingClientRect();
        const finalHeight = normRect.height;
        if (actualPlacement === 'top') {
            // Desired top so that bottom edge sits tipOffset above trigger top.
            const desiredTop = triggerRect.top - tipOffset - finalHeight;
            const currentTop = normRect.top;
            if (Math.abs(desiredTop - currentTop) > 1) {
                const correctedTop = Math.max(0, desiredTop);
                popover.style.top = `${correctedTop}px`;
                // Reposition tip again using corrected final height
                if (tipEnabled) {
                    popover.style.setProperty('--tip-top', `${finalHeight}px`);
                }
            }
        }
        else {
            // Bottom placement: ensure top equals trigger bottom + tipOffset (unless clamped by viewport bottom height reduction)
            const desiredTop = triggerRect.bottom + tipOffset;
            const currentTop = normRect.top;
            if (Math.abs(desiredTop - currentTop) > 1) {
                popover.style.top = `${desiredTop}px`;
                if (tipEnabled) {
                    popover.style.setProperty('--tip-top', `${tipBottomPlacement}px`);
                }
            }
        }
    }
    return actualPlacement;
};
export const usePopoverPosition = ({ ref, triggerRef, alignment, placement, open, withTip, mobileMaxContent }) => {
    const [actualPlacement, setActualPlacement] = useState(null);
    const lockedPlacementRef = useRef(null);
    // Initial measurement / placement lock
    useEffect(() => {
        if (!open)
            return;
        // First pass (may have zero-height content if not yet painted fully)
        const firstPassPlacement = updatePopoverPosition({
            ref,
            triggerRef,
            alignment,
            placement,
            withTip,
            mobileMaxContent
        });
        setActualPlacement(firstPassPlacement ?? null);
        lockedPlacementRef.current = firstPassPlacement ?? placement ?? 'bottom';
        // Second pass on next animation frame to catch late layout (fonts, images) & ensure viability flip engages with final size.
        let rafId = null;
        rafId = requestAnimationFrame(() => {
            if (!ref.current || !triggerRef.current)
                return;
            const secondPassPlacement = updatePopoverPosition({
                ref,
                triggerRef,
                alignment,
                // Use currently locked choice as preference, falling back to original requested or 'bottom'
                placement: (lockedPlacementRef.current ||
                    placement ||
                    'bottom'),
                withTip,
                mobileMaxContent
            });
            if (secondPassPlacement &&
                secondPassPlacement !== lockedPlacementRef.current) {
                lockedPlacementRef.current = secondPassPlacement;
                setActualPlacement(secondPassPlacement);
            }
        });
        return () => {
            if (rafId)
                cancelAnimationFrame(rafId);
        };
    }, [open, alignment, placement, withTip, mobileMaxContent, ref, triggerRef]);
    // Resize reposition (width/height adjustments) without flipping side
    useEffect(() => {
        if (!open)
            return;
        const handleResize = debounce(() => {
            if (!lockedPlacementRef.current)
                return;
            // Manual reposition using locked placement without running flip heuristic:
            const popover = ref.current;
            const trigger = triggerRef.current;
            if (!popover || !trigger)
                return;
            const BREAKPOINT_MD = 768;
            const windowInnerWidth = window.innerWidth;
            const windowInnerHeight = window.innerHeight;
            const triggerRect = trigger.getBoundingClientRect();
            const popoverRect = popover.getBoundingClientRect();
            const tipEnabled = Boolean(withTip) && windowInnerWidth >= BREAKPOINT_MD;
            const tipHeight = 11.5;
            const tipOffset = 20;
            const locked = lockedPlacementRef.current;
            let top;
            if (locked === 'top') {
                top = triggerRect.top - tipOffset - popoverRect.height;
                if (top < 0)
                    top = 0; // clamp
                if (tipEnabled) {
                    popover.style.setProperty('--tip-top', `${popoverRect.height}px`);
                }
            }
            else {
                top = triggerRect.bottom + tipOffset;
                if (tipEnabled) {
                    popover.style.setProperty('--tip-top', `${-tipHeight}px`);
                }
            }
            popover.style.top = `${top}px`;
            // Update available height variable
            const spaceAbove = triggerRect.top - tipOffset;
            const spaceBelow = windowInnerHeight - (triggerRect.bottom + tipOffset);
            const availableHeight = locked === 'top'
                ? Math.max(0, Math.floor(spaceAbove))
                : Math.max(0, Math.floor(spaceBelow));
            popover.style.setProperty('--available-height', `${availableHeight}px`);
            // Horizontal alignment recalculation (reuse original logic path by calling updatePopoverPosition partially)
            // For simplicity and to avoid duplication, call updatePopoverPosition again but ignore its flip result if it changes.
            const maybeNewPlacement = updatePopoverPosition({
                ref,
                triggerRef,
                alignment,
                placement: locked, // force requested side; function may flip if impossible, but we keep locked ref unchanged.
                withTip,
                mobileMaxContent
            });
            if (maybeNewPlacement && maybeNewPlacement !== locked) {
                // If impossibility occurred (e.g., extreme resize), update locked ref to avoid broken geometry.
                lockedPlacementRef.current = maybeNewPlacement;
                setActualPlacement(maybeNewPlacement);
            }
        }, 80);
        window.addEventListener('resize', handleResize, { passive: true });
        return () => {
            window.removeEventListener('resize', handleResize);
            handleResize.cancel();
        };
    }, [open, alignment, withTip, mobileMaxContent, ref, triggerRef]);
    return actualPlacement;
};
//# sourceMappingURL=use-popover-position.js.map